
import React, { useState } from 'react';
import { api } from '../api/client';

interface MarketEvent {
    id: string;
    date: string;
    time: string;
    currency: string;
    event: string;
    importance: number;
    actual?: string;
    forecast?: string;
    previous?: string;
}

interface Props {
    events: MarketEvent[];
    onRefresh: () => void;
}

const EconomicCalendarWidget: React.FC<Props> = ({ events, onRefresh }) => {
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [newEvent, setNewEvent] = useState<Partial<MarketEvent>>({
        date: new Date().toISOString().split('T')[0],
        currency: 'USD',
        importance: 2
    });

    // Group events by Date
    const groupedEvents: { [date: string]: MarketEvent[] } = {};
    events.forEach(e => {
        if (!groupedEvents[e.date]) groupedEvents[e.date] = [];
        groupedEvents[e.date].push(e);
    });

    // Sort dates
    const sortedDates = Object.keys(groupedEvents).sort();

    const handleFetch = async () => {
        if (!confirm('Investing.com에서 최신 데이터를 가져오시겠습니까?')) return;
        setLoading(true);
        try {
            await api.request('/market-data/calendar/fetch', { method: 'POST' });
            localStorage.setItem('last_market_update_ts', Date.now().toString());
            alert('업데이트 완료');
            onRefresh();
        } catch (e) {
            alert('업데이트 실패: ' + e);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveManual = async () => {
        if (!newEvent.event || !newEvent.date) return alert('이벤트명과 날짜는 필수입니다.');
        try {
            await api.request('/market-data/calendar', {
                method: 'POST',
                body: JSON.stringify(newEvent)
            });
            setModalOpen(false);
            onRefresh();
        } catch (e) {
            alert('저장 실패');
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('삭제하시겠습니까?')) return;
        try {
            await api.request(`/market-data/calendar?id=${id}`, { method: 'DELETE' });
            onRefresh();
        } catch (e) {
            alert('삭제 실패');
        }
    };

    return (
        <div className="card mb-lg p-lg">
            <div className="flex justify-between items-center mb-md">
                <h3 className="card-title">이번달 주요 경제 일정</h3>
                <div className="flex gap-sm">
                    <button
                        onClick={handleFetch}
                        disabled={loading}
                        className="btn btn-secondary text-sm"
                    >
                        {loading ? '가져오는 중...' : '🔄 최신 데이터 가져오기'}
                    </button>
                    <button
                        onClick={() => setModalOpen(true)}
                        className="btn btn-primary text-sm"
                    >
                        + 직접 추가
                    </button>
                </div>
            </div>

            {events.length === 0 ? (
                <div className="text-text-muted text-center py-md">
                    등록된 일정이 없습니다. 데이터를 가져오거나 직접 추가하세요.
                </div>
            ) : (
                <div className="space-y-md">
                    {sortedDates.map(date => (
                        <div key={date}>
                            <h3 className="text-md font-semibold mb-sm text-text-primary border-b border-border pb-xs">
                                {date}
                            </h3>
                            <div className="space-y-xs">
                                {groupedEvents[date].map(event => (
                                    <div key={event.id} className="flex justify-between items-center p-sm bg-background-light rounded hover:bg-background-hover group">
                                        <div className="flex items-center gap-sm flex-1">
                                            <span className="text-xs font-mono text-text-muted w-12">{event.time}</span>
                                            <span className={`text-xs font-bold w-10 ${event.currency === 'KRW' ? 'text-blue-400' : 'text-green-400'}`}>
                                                {event.currency}
                                            </span>
                                            <span className="text-sm font-medium flex-1">
                                                {event.event}
                                                {/* Importance Stars */}
                                                <span className="ml-xs text-yellow-500 text-xs">
                                                    {'★'.repeat(event.importance)}
                                                </span>
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-md text-xs">
                                            <div className="text-right hidden sm:block">
                                                <div className="text-text-muted">실제: <span className="text-text-primary">{event.actual || '-'}</span></div>
                                                <div className="text-text-muted">예측: {event.forecast || '-'}</div>
                                            </div>
                                            <button
                                                onClick={() => handleDelete(event.id)}
                                                className="text-text-muted hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Manual Add Modal */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-background-card p-lg rounded-lg w-[400px] border border-border shadow-xl">
                        <h3 className="text-lg font-bold mb-md">일정 직접 추가</h3>
                        <div className="space-y-sm">
                            <input
                                type="date"
                                className="input w-full"
                                value={newEvent.date}
                                onChange={e => setNewEvent({ ...newEvent, date: e.target.value })}
                            />
                            <input
                                type="time"
                                className="input w-full"
                                value={newEvent.time || ''}
                                onChange={e => setNewEvent({ ...newEvent, time: e.target.value })}
                            />
                            <div className="flex gap-sm">
                                <select
                                    className="input flex-1"
                                    value={newEvent.currency}
                                    onChange={e => setNewEvent({ ...newEvent, currency: e.target.value })}
                                >
                                    <option value="USD">USD</option>
                                    <option value="KRW">KRW</option>
                                </select>
                                <select
                                    className="input flex-1"
                                    value={newEvent.importance}
                                    onChange={e => setNewEvent({ ...newEvent, importance: Number(e.target.value) })}
                                >
                                    <option value={1}>★ Low</option>
                                    <option value={2}>★★ Med</option>
                                    <option value={3}>★★★ High</option>
                                </select>
                            </div>
                            <input
                                type="text"
                                className="input w-full"
                                placeholder="이벤트명 (예: FOMC 회의)"
                                value={newEvent.event || ''}
                                onChange={e => setNewEvent({ ...newEvent, event: e.target.value })}
                            />
                            <div className="flex gap-sm">
                                <input
                                    type="text"
                                    className="input flex-1"
                                    placeholder="실제"
                                    value={newEvent.actual || ''}
                                    onChange={e => setNewEvent({ ...newEvent, actual: e.target.value })}
                                />
                                <input
                                    type="text"
                                    className="input flex-1"
                                    placeholder="예측"
                                    value={newEvent.forecast || ''}
                                    onChange={e => setNewEvent({ ...newEvent, forecast: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-sm mt-lg">
                            <button onClick={() => setModalOpen(false)} className="btn btn-secondary">취소</button>
                            <button onClick={handleSaveManual} className="btn btn-primary">저장</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EconomicCalendarWidget;
